/**
 * "Данные" audio-transcript annotator (superuser) — parallel to CropAnnotator
 * but for the `audio-transcripts` dataset. Each clip shows the player + the
 * large-RU-model draft; the annotator fixes it into VERBATIM gold (what was
 * actually said) or marks it noise/inaudible (saved as "" — a blank training
 * target). The local transcripts.json is the source of truth; "Залить в ClearML"
 * ships it as a new version, "Удалить локальную копию" frees it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { Loader2, UploadCloud, Trash2, VolumeX, Check, AudioLines, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { API_BASE } from '@/api/base';
import { getRate } from '@/lib/playbackRate';
import RateButton from '@/components/RateButton';
import {
  getDownload, getAudioClips, annotateAudio, uploadNewVersion, deleteLocalCopy,
  type AudioClipItem,
} from '@/api/datasets';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import WaveClip from './WaveClip';

interface Props { downloadId: number; onClose?: () => void; }

export default function AudioTranscriber({ downloadId, onClose }: Props) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [waveOpen, setWaveOpen] = useState<Set<number>>(new Set());
  const toggleWave = (rid: number) =>
    setWaveOpen((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid);
      else next.add(rid);
      return next;
    });

  const dlSwr = useSWR(['dl', downloadId], () => getDownload(downloadId).then(r => r.data));
  const clipsSwr = useSWR(
    ['audio-clips', downloadId],
    () => getAudioClips(downloadId).then(r => r.data),
    { revalidateOnFocus: false, revalidateIfStale: false },
  );

  // Pin each clip's audio src to its FIRST URL: the token is re-signed on every
  // fetch, so without this a revalidation mid-playback would swap the <audio src>
  // and the browser would reload (truncating) the clip.
  const srcCache = useRef(new Map<number, string>());
  const pinnedSrc = (rid: number, url: string) => {
    if (!srcCache.current.has(rid)) srcCache.current.set(rid, `${API_BASE}${url}`);
    return srcCache.current.get(rid)!;
  };
  const dd = dlSwr.data;
  const data = clipsSwr.data;
  const items: AudioClipItem[] = data?.items ?? [];

  // Seed editable drafts from corrected_text ?? model_text once loaded.
  useEffect(() => {
    if (!items.length) return;
    setDrafts((prev) => {
      const next = { ...prev };
      for (const it of items) {
        if (next[it.audio_record_id] === undefined) {
          next[it.audio_record_id] = it.corrected_text ?? it.model_text ?? '';
        }
      }
      return next;
    });
  }, [items]);

  const labeledCount = useMemo(() => items.filter((i) => i.labeled).length, [items]);

  const save = async (rid: number, text: string | null) => {
    setSavingId(rid);
    try {
      await annotateAudio(downloadId, [{ audio_record_id: rid, corrected_text: text }]);
      // Optimistic LOCAL update only — a full clipsSwr.mutate() refetches every
      // clip (re-mints all tokens, rebuilds every row) on each save, janking the
      // page. Patch just the saved clip; no revalidation.
      clipsSwr.mutate((cur) => {
        if (!cur) return cur;
        const its = cur.items.map((it) =>
          it.audio_record_id === rid ? { ...it, corrected_text: text ?? '', labeled: true } : it);
        return { ...cur, items: its, labeled: its.filter((i) => i.labeled).length };
      }, { revalidate: false });
    } finally {
      setSavingId(null);
    }
  };

  const onUpload = async () => {
    setBusy(true);
    setUploadMsg(null);
    try {
      // The upload runs as a celery task (202 → status=uploading). Poll until it
      // leaves UPLOADING, then classify by status_detail: the task lands back on
      // READY whether it succeeded or failed, and writes "upload failed: …" into
      // status_detail only on failure.
      await uploadNewVersion(downloadId, 'transcript corrections');
      let dd = (await getDownload(downloadId)).data;
      for (let i = 0; i < 60 && dd.status === 'uploading'; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        dd = (await getDownload(downloadId)).data;
      }
      await dlSwr.mutate(dd, { revalidate: false });
      if (dd.status === 'uploading') {
        setUploadMsg({ kind: 'err', text: 'Загрузка ещё идёт — обновите страницу через минуту.' });
      } else if (dd.status_detail) {
        setUploadMsg({ kind: 'err', text: dd.status_detail });
      } else {
        setUploadMsg({
          kind: 'ok',
          text: `Залито в ClearML${dd.new_version_id ? ` · новая версия ${dd.new_version_id.slice(0, 12)}` : ''}.`,
        });
      }
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } }; message?: string });
      setUploadMsg({ kind: 'err', text: detail?.response?.data?.detail || detail?.message || 'Не удалось запустить загрузку.' });
    } finally {
      setBusy(false);
    }
  };
  const onDelete = async () => {
    if (!window.confirm('Удалить локальную копию? Несохранённые в ClearML правки пропадут.')) return;
    setBusy(true);
    try { await deleteLocalCopy(downloadId); onClose?.(); }
    finally { setBusy(false); }
  };

  if (clipsSwr.isLoading || dlSwr.isLoading) {
    return <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
    </div>;
  }
  if (clipsSwr.error || !data) {
    return <div className="p-6 text-sm text-destructive">
      {dd && dd.status !== 'ready' ? `Копия не готова (статус: ${dd.status}).` : t('error.general')}
    </div>;
  }

  return (
    <div className="space-y-4">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/30 px-4 py-2">
        <div className="text-sm">
          <span className="font-medium">{data.dataset.name}</span>
          <span className="ml-2 text-muted-foreground tabular-nums">
            {labeledCount}/{data.total} размечено
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onUpload} disabled={busy}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <UploadCloud className="size-4" />}
            Залить в ClearML
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} disabled={busy}>
            <Trash2 className="size-4" /> Удалить копию
          </Button>
        </div>
      </div>

      {uploadMsg && (
        <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
          uploadMsg.kind === 'ok'
            ? 'border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-300'
            : 'border-destructive/40 bg-destructive/10 text-destructive'
        }`}>
          {uploadMsg.kind === 'ok'
            ? <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
            : <AlertTriangle className="mt-0.5 size-4 shrink-0" />}
          <span className="break-words">{uploadMsg.text}</span>
          <button type="button" onClick={() => setUploadMsg(null)} className="ml-auto text-xs underline opacity-70 hover:opacity-100">скрыть</button>
        </div>
      )}

      {/* clip list */}
      <div className="divide-y rounded-md border">
        {items.map((clip) => {
          const rid = clip.audio_record_id;
          const draft = drafts[rid] ?? '';
          const dirty = clip.corrected_text == null
            ? draft !== (clip.model_text ?? '')
            : draft !== clip.corrected_text;
          // Unlabeled clips are savable even when the draft is unchanged — that's
          // "accept the model draft as gold" in one click (no fake edit needed).
          // (dirty still gates auto-save-on-blur so blurring never auto-accepts.)
          const canSave = clip.corrected_text == null || dirty;
          const accepting = clip.corrected_text == null && !dirty;
          return (
            <div key={rid} className="flex flex-col gap-2 p-3 md:flex-row md:items-start">
              <div className="flex w-full shrink-0 flex-col gap-1 md:w-80">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-mono">#{rid}</span>
                  {clip.recorded_at && <span>{new Date(clip.recorded_at).toLocaleString('ru-RU', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>}
                  {clip.labeled && <span className="inline-flex items-center gap-0.5 text-green-600 dark:text-green-400"><Check className="size-3" />размечено</span>}
                </div>
                <div className="flex items-center gap-1"><audio controls preload="none" src={pinnedSrc(rid, clip.audio_url)} className="h-8 w-full"
                  onPlay={(e) => { e.currentTarget.playbackRate = getRate(); }} /><RateButton /></div>
                <button type="button" onClick={() => toggleWave(rid)}
                  className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
                  <AudioLines className="size-3" /> {waveOpen.has(rid) ? 'скрыть волну' : 'волна / участки'}
                </button>
                {waveOpen.has(rid) && (
                  <WaveClip src={pinnedSrc(rid, clip.audio_url)} segments={clip.model_segments ?? []} />
                )}
                {clip.small_model_text && (
                  <div className="text-[11px] text-muted-foreground">
                    <span className="uppercase tracking-wide">малая модель:</span> {clip.small_model_text}
                  </div>
                )}
              </div>
              <div className="flex w-full flex-1 flex-col gap-1">
                <Textarea
                  value={draft}
                  onChange={(e) => setDrafts((p) => ({ ...p, [rid]: e.target.value }))}
                  rows={2}
                  placeholder="Дословная расшифровка (что реально сказано)…"
                  className="text-sm"
                />
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="secondary" disabled={!canSave || savingId === rid}
                    onClick={() => save(rid, draft)}>
                    {savingId === rid ? <Loader2 className="size-3 animate-spin" /> : null} {accepting ? 'Принять' : 'Сохранить'}
                  </Button>
                  <Button size="sm" variant="ghost" disabled={savingId === rid}
                    title="Шум / неразборчиво → пустая цель (модель учится игнорировать)"
                    onClick={() => { setDrafts((p) => ({ ...p, [rid]: '' })); save(rid, ''); }}>
                    <VolumeX className="size-4" /> Шум/неразборчиво
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        {!items.length && (
          <div className="p-6 text-center text-sm text-muted-foreground">Нет клипов в копии.</div>
        )}
      </div>
    </div>
  );
}
